/**
 * What a shop's stock looks like on screen: the entry shape the server sends, the readable names and prices,
 * and the filtering. The shop itself is somebody in the world now (`pages/ShopsPanel.tsx`) — there is no shop
 * on the character sheet. The client carries no price list: every name and price is the server's.
 */

import { optionLabel } from './options.js';

export interface ShopEntry {
  readonly id: string;
  /** The name a person reads, from the server (`itemDisplayName`); the id is the identity and loses punctuation. */
  readonly name?: string;
  readonly category: string;
  readonly priceCp: number;
  /** The price spoken in coin — "75 gp" — by the server, never computed here. */
  readonly price: string;
  readonly weightLb?: number;
}

export const SHOP_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  weapon: 'Weapons',
  ammunition: 'Ammunition',
  armour: 'Armour',
  shield: 'Shields',
  helmet: 'Helmets',
  clothing: 'Clothing',
  equipment: 'Equipment',
  provisions: 'Provisions',
  tack: 'Tack and harness',
};

/** What to call an entry on screen: the server's name, falling back to the key made readable. */
export const shopName = (entry: { readonly id: string; readonly name?: string }): string =>
  entry.name ?? optionLabel(entry.id);

/** A price as the shop shows it: a thing the book prices at nothing is "free", not "0 gp". */
export const priceText = (entry: { readonly priceCp: number; readonly price: string }): string =>
  entry.priceCp === 0 ? 'free' : entry.price;

/**
 * The entries in one category (or all) whose name contains the search text.
 * Pure, so the listing is testable without a network.
 */
export function visibleEntries(
  entries: readonly ShopEntry[],
  category: string,
  search: string,
): readonly ShopEntry[] {
  const needle = search.trim().toLowerCase();
  return entries.filter(
    (entry) =>
      (category === '' || entry.category === category) &&
      (needle === '' ||
        entry.id.toLowerCase().includes(needle) ||
        shopName(entry).toLowerCase().includes(needle)),
  );
}
